import {
  ORDER_STATUS_LABELS,
  LEAD_STATUS_LABELS,
  type OrderStatus,
  type LeadStatus,
} from "@ckgo2/shared/types";
import { enumLabel } from "@/i18n/enumLabels";

// Presentation layer for order/lead statuses. Enum values live in @ckgo2/shared
// (consumed by API + web too); labels resolve through enumLabel() so they follow
// the admin language (a language switch reloads the page, so module scope is
// safe). Tailwind classes are admin-only, so they stay here. Single source for
// every order/lead badge + kanban board.

export interface StatusBadge {
  label: string;
  className: string;
}

export interface KanbanColumn<S extends string> {
  id: S;
  label: string;
  color: string; // dot / accent
  bgColor: string; // column background
}

// ── Orders ────────────────────────────────────────────────────────────────
const ORDER_BADGE_CLASSNAMES: Record<OrderStatus, string> = {
  inquiry: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300",
  quoted: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  confirmed: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
  deposit_paid: "bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-400",
  fully_paid: "bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400",
  completed: "bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400",
  cancelled: "bg-error-100 text-error-700 dark:bg-error-900/30 dark:text-error-400",
};

export const ORDER_STATUS_CONFIG: Record<OrderStatus, StatusBadge> = Object.fromEntries(
  (Object.keys(ORDER_STATUS_LABELS) as OrderStatus[]).map((s) => [
    s,
    { label: enumLabel(ORDER_STATUS_LABELS, s), className: ORDER_BADGE_CLASSNAMES[s] },
  ]),
) as Record<OrderStatus, StatusBadge>;

// Board columns = the active pipeline (cancelled is a terminal side-state, not a column).
export const ORDER_KANBAN_COLUMNS: KanbanColumn<OrderStatus>[] = [
  {
    id: "inquiry",
    label: enumLabel(ORDER_STATUS_LABELS, "inquiry"),
    color: "bg-gray-500",
    bgColor: "bg-gray-50 dark:bg-gray-800/50",
  },
  {
    id: "quoted",
    label: enumLabel(ORDER_STATUS_LABELS, "quoted"),
    color: "bg-blue-500",
    bgColor: "bg-blue-50 dark:bg-blue-900/10",
  },
  {
    id: "confirmed",
    label: enumLabel(ORDER_STATUS_LABELS, "confirmed"),
    color: "bg-purple-500",
    bgColor: "bg-purple-50 dark:bg-purple-900/10",
  },
  {
    id: "deposit_paid",
    label: enumLabel(ORDER_STATUS_LABELS, "deposit_paid"),
    color: "bg-amber-500",
    bgColor: "bg-amber-50 dark:bg-amber-900/10",
  },
  {
    id: "fully_paid",
    label: enumLabel(ORDER_STATUS_LABELS, "fully_paid"),
    color: "bg-green-500",
    bgColor: "bg-green-50 dark:bg-green-900/10",
  },
  {
    id: "completed",
    label: enumLabel(ORDER_STATUS_LABELS, "completed"),
    color: "bg-emerald-500",
    bgColor: "bg-emerald-50 dark:bg-emerald-900/10",
  },
];

// ── Leads ─────────────────────────────────────────────────────────────────
const LEAD_BADGE_CLASSNAMES: Record<LeadStatus, string> = {
  received: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300",
  quoted: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  call_back: "bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-400",
  dormant: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
  sold: "bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400",
  lost: "bg-error-100 text-error-700 dark:bg-error-900/30 dark:text-error-400",
};

export const LEAD_STATUS_CONFIG: Record<LeadStatus, StatusBadge> = Object.fromEntries(
  (Object.keys(LEAD_STATUS_LABELS) as LeadStatus[]).map((s) => [
    s,
    { label: enumLabel(LEAD_STATUS_LABELS, s), className: LEAD_BADGE_CLASSNAMES[s] },
  ]),
) as Record<LeadStatus, StatusBadge>;

// Lead board = the funnel. `lost` is terminal (shown as a count, not a column).
export const LEAD_KANBAN_COLUMNS: KanbanColumn<LeadStatus>[] = [
  {
    id: "received",
    label: enumLabel(LEAD_STATUS_LABELS, "received"),
    color: "bg-gray-500",
    bgColor: "bg-gray-50 dark:bg-gray-800/50",
  },
  {
    id: "quoted",
    label: enumLabel(LEAD_STATUS_LABELS, "quoted"),
    color: "bg-blue-500",
    bgColor: "bg-blue-50 dark:bg-blue-900/10",
  },
  {
    id: "call_back",
    label: enumLabel(LEAD_STATUS_LABELS, "call_back"),
    color: "bg-amber-500",
    bgColor: "bg-amber-50 dark:bg-amber-900/10",
  },
  {
    id: "dormant",
    label: enumLabel(LEAD_STATUS_LABELS, "dormant"),
    color: "bg-slate-500",
    bgColor: "bg-slate-50 dark:bg-slate-800/50",
  },
  {
    id: "sold",
    label: enumLabel(LEAD_STATUS_LABELS, "sold"),
    color: "bg-green-500",
    bgColor: "bg-green-50 dark:bg-green-900/10",
  },
];
